'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { useEffect, useRef } from 'react'
import useSWR from 'swr'

import { type DataLayer } from '@redteclab/gtm-datalayer-library'

import { apmCaptureError } from '../../apm'
import { gtmApiUrlFetcher, type GtmEventData } from '../../gtm'

import { useCartIntermediateContext } from './useCartIntermediateContext'

const fetchCartIntermediateGtmDataErrorApmLogger = (error: unknown): void => {
  apmCaptureError(
    new Error(
      'CART_INTERMEDIATE_GTM_DATA_FETCH_FAIL: failed to fetch data from gtm cartintermediate endpoint',
      {
        cause: error,
      },
    ),
  )
}

/**
 * Initial gtm data is built when CartIntermediateContext is initialized
 * therefore we need to replace requestUrl with new one built based on
 * entries added to the cart, and their data is collected once addToCart button is clicked.
 */
const prepareModifiedCartIntermediateGtmEventData = ({
  dataRequestUrl,
  gtmEventData,
}: {
  dataRequestUrl: string
  gtmEventData: DataLayer
}): GtmEventData | null => {
  const requestUrl = encodeURIComponent(dataRequestUrl)

  return {
    ...gtmEventData,
    navigation: {
      ...gtmEventData.navigation,
      requestUrl,
    },
  }
}

/**
 * Hook to fetch GTM data when the cart intermediate drawer is toggled (only from closed to opened)
 * and the product was successfully added to the cart. It pushes a GTM event with merged cartintermediate
 * context and fetched data.
 */
export const useCartIntermediateDrawerFetchGtmDataAndPushToDataLayer =
  (): void => {
    const {
      cartIntermediateDataLayerInitialData,
      cartIntermediateOpenableControls: { isOpen },
      gtmDataLayerCartIntermediateUrlData,
      isDataSuccessfullyAddedToCart,
    } = useCartIntermediateContext()

    const gtmApiUrl =
      gtmDataLayerCartIntermediateUrlData?.cartIntermediateGtmDataApiUrl
    const dataRequestUrl =
      gtmDataLayerCartIntermediateUrlData?.cartIntermediateRequestUrl

    const apiUrlMutation = useSWR(gtmApiUrl, gtmApiUrlFetcher)
    const prevIsDrawerOpen = useRef<boolean>(false)

    useEffect(() => {
      if (
        !dataRequestUrl ||
        !isDataSuccessfullyAddedToCart ||
        (!isOpen && prevIsDrawerOpen.current)
      ) {
        return
      }

      // Function to fetch GTM data and push event if drawer state has changed
      const fetchAndPush = async (): Promise<void> => {
        // eslint-disable-next-line unicorn/try-complexity -- don't reference, refactor if touched
        try {
          const data = await apiUrlMutation.mutate()
          const gtmEventModified = prepareModifiedCartIntermediateGtmEventData({
            dataRequestUrl,
            gtmEventData: cartIntermediateDataLayerInitialData,
          })

          if (data && gtmEventModified) {
            // Merge context data and fetched GTM data and push it to the data layer
            sendGTMEvent({ ...gtmEventModified, ...data })
          }
        } catch (error) {
          fetchCartIntermediateGtmDataErrorApmLogger(error)
        } finally {
          prevIsDrawerOpen.current = isOpen
        }
      }
      void fetchAndPush()
    }, [
      isOpen,
      apiUrlMutation,
      gtmApiUrl,
      isDataSuccessfullyAddedToCart,
      dataRequestUrl,
      cartIntermediateDataLayerInitialData,
    ])
  }
